import { useState } from "react";
import { ArrowUpRight, Info } from "lucide-react";
import { Reveal } from "./primitives";
import { TERMINAL_URL } from "./orderFlowData";

const VIEWS = [
  { id: "liquidation", label: "Liquidation map", img: "/of/liquidation.webp" },
  { id: "heatmap", label: "Resting-order heatmap", img: "/of/heatmap.webp" },
];

const POINTS = [
  ["Live positions", "Entry, leverage, liquidation price and PnL for each tracked wallet"],
  ["Liquidation map", "Where tracked leverage gets forced out, plotted on price"],
  ["TP / SL marks", "Resting take-profit and stop levels from tracked wallets"],
  ["Depth + heatmap", "Resting orders over time, 12H to 30D"],
];

export const HyperliquidLayer = () => {
  const [view, setView] = useState(VIEWS[0].id);
  const v = VIEWS.find((x) => x.id === view);
  return (
    <section id="hyperliquid" data-testid="hl-layer" className="relative border-t border-white/[0.06] bg-[#0D0E11] py-24 md:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-6 md:px-12 lg:grid-cols-[1fr_1.35fr] lg:items-center">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">The differentiator · Hyperliquid layer</p>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.02] tracking-[-0.03em] text-[#ECEBE4] md:text-5xl">
            The whale book competitors <span className="text-[#7D8390]">don't show.</span>
          </h2>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-[#9DA3AE]">
            Tracked Hyperliquid whale wallets are followed live and their positions are drawn straight onto your chart. You see who is
            leveraged, where, and where they get liquidated. A built-in walkthrough explains every layer.
          </p>
          <dl className="mt-8 grid gap-px bg-white/[0.06] sm:grid-cols-2">
            {POINTS.map(([t, d]) => (
              <div key={t} className="bg-[#0D0E11] py-4 pr-4 sm:odd:pr-6">
                <dt className="font-mono text-xs uppercase tracking-[0.16em] text-[#ECEBE4]">{t}</dt>
                <dd className="mt-1.5 font-mono text-[11px] leading-relaxed text-[#7D8390]">{d}</dd>
              </div>
            ))}
          </dl>
          <p data-testid="hl-caveat" className="mt-6 flex gap-2 font-mono text-[11px] leading-relaxed text-[#7D8390]">
            <Info size={13} className="mt-0.5 shrink-0" />
            Covers tracked wallets only, not every position on Hyperliquid. Separate venue from the chart's exchange data.
          </p>
          <a href={TERMINAL_URL} data-testid="hl-cta" className="group mt-8 inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.16em] text-[#ECEBE4] transition-[border-color,background-color] duration-300 hover:border-white/50 hover:bg-white/[0.04]">
            See the whale layer live <ArrowUpRight size={15} />
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mb-3 flex gap-1.5">
            {VIEWS.map((x) => (
              <button key={x.id} data-testid={`hl-view-${x.id}`} onClick={() => setView(x.id)} className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${x.id === view ? "border-white/30 text-[#ECEBE4]" : "border-white/[0.08] text-[#5A6170] hover:text-[#9DA3AE]"}`}>
                {x.label}
              </button>
            ))}
          </div>
          <div className="premium-card overflow-hidden border border-white/[0.08]">
            <img key={v.id} src={v.img} alt={v.label} width={1600} height={900} className="block h-auto w-full animate-[fadeIn_.5s_ease]" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
